import React, { useEffect } from "react";
import "leaflet/dist/leaflet.css";
import "../styles/ContactPage.scss";
import { useContacts } from "../Apis/useApiQueries";
import ContactForm from "../Components/ContactForm";
import LeafletMap from "../Components/LeafletMap";
import { useFooterData } from "../Apis/useApiQueries";
import { metaPixelEvents } from "../utils/metaPixel";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaMapMarkerAlt,
  FaStethoscope,
  FaUserMd,
  FaCalendarAlt,
  FaWhatsapp,
} from "react-icons/fa";

const ContactUs = () => {
  const { data: contacts, error: contactError } = useContacts();
  const { siteDetails } = useFooterData();

  // Track contact page view
  useEffect(() => {
    metaPixelEvents.trackContactInfoView();
    metaPixelEvents.trackLocationView();
  }, []);

  if (!contacts || contactError) return null; // Ensure it doesn't break the render

  return (
    <div className="modern-contact-page">
      {/* Hero Section */}
      <div className="contact-hero">
        {/* Animated Background Elements */}
        <div className="hero-bg-elements">
          <div className="bg-shape bg-shape-1"></div>
          <div className="bg-shape bg-shape-2"></div>
          <div className="bg-shape bg-shape-3"></div>
          <div className="bg-particles">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
          </div>
        </div>

        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">
              <FaUserMd className="badge-icon" />
              <span>ייעוץ מקצועי</span>
            </div>
            <h1>
              <span className="highlight">צרו איתנו קשר</span>
              <span className="subtitle">ותתחילו את המסע לחיוך מושלם</span>
            </h1>
            <p className="hero-description">
              אנחנו כאן כדי לענות על כל שאלה ולהדריך אותכם בבחירת הטיפול המתאים
              ביותר. צוות המומחים שלנו ישמח לספק לכם ייעוץ אישי ומקצועי ללא כל
              התחייבות.
            </p>
            <div className="hero-actions">
              <a
                href="https://wa.me/+972532268138"
                target="_blank"
                rel="noopener noreferrer"
                className="hero-cta primary"
              >
                <FaWhatsapp className="cta-icon" />
                <span>דברו איתנו בווטסאפ</span>
              </a>
              <a
                href={`tel:${siteDetails?.data?.phone || "053-226-8138"}`}
                className="hero-cta secondary"
              >
                <FaPhoneAlt className="cta-icon" />
                <span>התקשרו עכשיו</span>
              </a>
            </div>
          </div>

          <div className="hero-visual">
            <div className="visual-container">
              {/* Central medical icon */}
              <div className="central-icon">
                <FaUserMd />
              </div>

              {/* Orbiting icons */}
              <div className="orbiting-icons">
                <div className="orbit-icon orbit-icon-1">
                  <FaStethoscope />
                </div>
                <div className="orbit-icon orbit-icon-2">
                  <FaCalendarAlt />
                </div>
                <div className="orbit-icon orbit-icon-3">
                  <FaPhoneAlt />
                </div>
                <div className="orbit-icon orbit-icon-4">
                  <FaWhatsapp />
                </div>
              </div>

              {/* Pulse rings */}
              <div className="pulse-rings">
                <div className="pulse-ring ring-1"></div>
                <div className="pulse-ring ring-2"></div>
                <div className="pulse-ring ring-3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="contact-form-section">
        <div className="container">
          <div className="section-header-centered">
            <div className="header-badge">
              <FaUserMd className="badge-icon" />
              <span>יצירת קשר</span>
            </div>
            <h2>השאירו פרטים</h2>
            <p className="section-subtitle">
              מלאו את הפרטים שלכם ונחזור אליכם בהקדם האפשרי לתיאום פגישת ייעוץ
            </p>
          </div>

          <div className="form-content">
            <div className="form-benefits-enhanced">
              <div className="benefits-grid">
                <div className="benefit-card">
                  <div className="benefit-icon-wrapper">
                    <FaStethoscope className="benefit-icon" />
                  </div>
                  <div className="benefit-content">
                    <h3>ייעוץ מקצועי ללא עלות</h3>
                    <p>קבלו ייעוץ מקצועי ומפורט ללא כל התחייבות כספית</p>
                  </div>
                </div>

                <div className="benefit-card">
                  <div className="benefit-icon-wrapper">
                    <FaCalendarAlt className="benefit-icon" />
                  </div>
                  <div className="benefit-content">
                    <h3>תיאום מועד נוח</h3>
                    <p>נתאם איתכם פגישה במועד הנוח לכם ביותר</p>
                  </div>
                </div>

                <div className="benefit-card">
                  <div className="benefit-icon-wrapper">
                    <FaUserMd className="benefit-icon" />
                  </div>
                  <div className="benefit-content">
                    <h3>הדרכה אישית</h3>
                    <p>קבלו הדרכה אישית ומותאמת לצרכים שלכם</p>
                  </div>
                </div>

                <div className="benefit-card">
                  <div className="benefit-icon-wrapper">
                    <FaClock className="benefit-icon" />
                  </div>
                  <div className="benefit-content">
                    <h3>שעות פעילות</h3>
                    <div
                      className="hours-content"
                      dangerouslySetInnerHTML={{
                        __html:
                          siteDetails?.data?.openingHours ||
                          "ראשון-חמישי: 9:00-18:00",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-wrapper-enhanced">
              <div className="form-header">
                <h3>מלאו את הפרטים</h3>
                <p>נשמח לעזור לכם למצוא את הפתרון המושלם</p>
              </div>
              {contacts.length > 0 ? (
                <ContactForm />
              ) : (
                <p>No contact details available.</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Locations & Map Section */}
      <div className="locations-section">
        <div className="container">
          <div className="section-header">
            <h2>המרפאות שלנו</h2>
            <p>שתי מרפאות מתקדמות במיקומים נוחים</p>
          </div>

          <div className="locations-content">
            <div className="locations-grid">
              <div className="location-card herzliya">
                <div className="location-header">
                  <div className="location-badge">מרכז</div>
                  <h3>מרפאת הרצליה</h3>
                </div>
                <div className="location-details">
                  <div className="address-info">
                    <FaMapMarkerAlt className="location-icon" />
                    <span>רחוב סוקולוב 27, הרצליה</span>
                  </div>
                  <div className="location-features">
                    <span className="feature">במרכז העיר</span>
                    <span className="feature">נגישות מלאה</span>
                    <span className="feature">תחבורה ציבורית</span>
                  </div>
                  <div className="location-map">
                    <LeafletMap location="herzliya" height="250px" />
                  </div>
                  <div className="navigation-buttons">
                    <a
                      href="https://waze.com/ul?q=רחוב סוקולוב 27 הרצליה&navigate=yes"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-btn waze"
                    >
                      <img src="/images/waze-icon.svg" alt="Waze" />
                      Waze
                    </a>
                    <a
                      href="https://maps.google.com/?q=רחוב סוקולוב 27, הרצליה"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-btn maps"
                    >
                      <img
                        src="/images/google-maps-icon.svg"
                        alt="Google Maps"
                      />
                      Maps
                    </a>
                  </div>
                </div>
              </div>

              <div className="location-card beersheba">
                <div className="location-header">
                  <div className="location-badge">דרום</div>
                  <h3>מרפאת באר שבע</h3>
                </div>
                <div className="location-details">
                  <div className="address-info">
                    <FaMapMarkerAlt className="location-icon" />
                    <span>אליהו נאוי 24 (מתחם וורקיז קומת כניסה), באר שבע</span>
                  </div>
                  <div className="location-features">
                    <span className="feature">במרכז העיר</span>
                    <span className="feature">נגישות מלאה</span>
                    <span className="feature">חניה בשפע</span>
                  </div>
                  <div className="location-map">
                    <LeafletMap location="beersheba" height="250px" />
                  </div>
                  <div className="navigation-buttons">
                    <a
                      href="https://waze.com/ul?q=אליהו נאוי 24 באר שבע&navigate=yes"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-btn waze"
                    >
                      <img src="/images/waze-icon.svg" alt="Waze" />
                      Waze
                    </a>
                    <a
                      href="https://maps.google.com/?q=אליהו נאוי 24, באר שבע"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-btn maps"
                    >
                      <img
                        src="/images/google-maps-icon.svg"
                        alt="Google Maps"
                      />
                      Maps
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
